import {announce} from '@github-ui/aria-live'
import {createContext, useCallback, use, useEffect, useMemo, useRef, useState} from 'react'

type SubIssueFocusRequest = {
  issueId: string
  token: number
}

type PendingSubIssueFocusRequest = SubIssueFocusRequest & {
  expectedFocus: Element | null
  message: string
  previousCurrentIssueId: string | null
}

type SubIssueFocusOperation = PendingSubIssueFocusRequest & {
  focusedElement: Element | null
}

export type SubIssueStateContext = {
  createDialogOpen: boolean
  activeIssueId: string | undefined
  currentSubIssueId: string | null
  focusRequest: SubIssueFocusRequest | null
  closeCreateDialog: () => void
  openCreateDialog: (issueId: string) => void
  focusSubIssue: (issueId: string, message: string, expectedFocus?: Element | null) => number | null
  claimFocusRequest: (token: number) => boolean
  completeFocusRequest: (token: number, focused: boolean) => void
  remapSubIssueFocus: (token: number, issueId: string) => void
  rollbackSubIssueFocus: (token: number) => void
  setCurrentSubIssue: (issueId: string, element?: Element | null) => void
}

let nextFocusRequestToken = 0

/**
 * Context sub-issues-related state in the issue viewer.
 */
export const SubIssueStateContext = createContext<SubIssueStateContext | undefined>(undefined)

export function SubIssueStateProvider({children}: {children: React.ReactNode}) {
  const [activeIssueId, setActiveIssueId] = useState<string | undefined>(undefined)
  const [currentSubIssueId, setCurrentSubIssueId] = useState<string | null>(null)
  const [focusRequest, setFocusRequest] = useState<PendingSubIssueFocusRequest | null>(null)
  const focusRequestRef = useRef<PendingSubIssueFocusRequest | null>(null)
  const focusOperationsRef = useRef(new Map<number, SubIssueFocusOperation>())
  const currentSubIssueIdRef = useRef<string | null>(null)
  const currentFocusTokenRef = useRef<number | null>(null)
  const activeIssueIdRef = useRef(activeIssueId)
  // Where focus landed once the create dialog closed. The request finishes later, and focus only
  // moves to the confirmation if it is still there, so it never pulls someone away from where they went next.
  const focusAfterDialogCloseRef = useRef<Element | null>(null)
  const dialogHasOpenedRef = useRef(false)
  useEffect(() => {
    activeIssueIdRef.current = activeIssueId
    if (activeIssueId !== undefined) {
      dialogHasOpenedRef.current = true
      return
    }
    if (!dialogHasOpenedRef.current) return
    // Wait a frame so the dialog has handed focus back first.
    const frame = requestAnimationFrame(() => {
      focusAfterDialogCloseRef.current = document.activeElement
    })
    return () => {
      cancelAnimationFrame(frame)
      focusAfterDialogCloseRef.current = null
    }
  }, [activeIssueId])

  const createDialogOpen = activeIssueId !== undefined

  const openCreateDialog = useCallback((issueId: string) => {
    focusRequestRef.current = null
    setFocusRequest(null)
    activeIssueIdRef.current = issueId
    setActiveIssueId(issueId)
  }, [])
  const closeCreateDialog = useCallback(() => {
    activeIssueIdRef.current = undefined
    setActiveIssueId(undefined)
  }, [])
  const clearFocusRequest = useCallback((request: PendingSubIssueFocusRequest, announceFallback: boolean) => {
    if (focusRequestRef.current?.token !== request.token) return
    focusRequestRef.current = null
    setFocusRequest(null)
    if (announceFallback) announce(request.message)
  }, [])
  const focusSubIssue = useCallback((issueId: string, message: string, expectedFocus?: Element | null) => {
    // "Create more" keeps focus in the open dialog, which provides its own success feedback.
    if (activeIssueIdRef.current !== undefined) return null
    const expected = expectedFocus ?? focusAfterDialogCloseRef.current ?? document.activeElement
    const active = document.activeElement
    if (expected && active && active !== document.body && active !== expected) {
      announce(message)
      return null
    }
    const request = {
      issueId,
      message,
      expectedFocus: expected,
      previousCurrentIssueId: currentSubIssueIdRef.current,
      token: nextFocusRequestToken++,
    }
    focusOperationsRef.current.set(request.token, {...request, focusedElement: null})
    focusRequestRef.current = request
    currentSubIssueIdRef.current = issueId
    currentFocusTokenRef.current = request.token
    setCurrentSubIssueId(issueId)
    setFocusRequest(request)
    return request.token
  }, [])
  const claimFocusRequest = useCallback(
    (token: number) => {
      const request = focusRequestRef.current
      if (!request || request.token !== token) return false
      const active = document.activeElement
      if (
        activeIssueIdRef.current !== undefined ||
        (request.expectedFocus && active && active !== document.body && active !== request.expectedFocus)
      ) {
        if (currentFocusTokenRef.current === token) {
          currentSubIssueIdRef.current = request.previousCurrentIssueId
          currentFocusTokenRef.current = null
          setCurrentSubIssueId(request.previousCurrentIssueId)
        }
        clearFocusRequest(request, activeIssueIdRef.current === undefined)
        return false
      }
      return true
    },
    [clearFocusRequest],
  )
  const completeFocusRequest = useCallback(
    (token: number, focused: boolean) => {
      const request = focusRequestRef.current
      if (!request || request.token !== token) return
      if (!focused && currentFocusTokenRef.current === token) {
        currentSubIssueIdRef.current = request.previousCurrentIssueId
        currentFocusTokenRef.current = null
        setCurrentSubIssueId(request.previousCurrentIssueId)
      }
      clearFocusRequest(request, !focused)
    },
    [clearFocusRequest],
  )
  const remapSubIssueFocus = useCallback((token: number, issueId: string) => {
    const operation = focusOperationsRef.current.get(token)
    if (!operation) return
    const previousIssueId = operation.issueId
    operation.issueId = issueId

    if (currentFocusTokenRef.current === token && currentSubIssueIdRef.current === previousIssueId) {
      currentSubIssueIdRef.current = issueId
      currentFocusTokenRef.current = null
      setCurrentSubIssueId(issueId)
    }
    if (focusRequestRef.current?.token === token) {
      const remappedRequest = {...focusRequestRef.current, issueId}
      focusRequestRef.current = remappedRequest
      setFocusRequest(remappedRequest)
    }
    focusOperationsRef.current.delete(token)
  }, [])
  const rollbackSubIssueFocus = useCallback((token: number) => {
    const operation = focusOperationsRef.current.get(token)
    if (!operation) return
    focusOperationsRef.current.delete(token)

    const active = document.activeElement
    const ownedFocusedElement = operation.focusedElement
    const shouldRestoreFocus =
      ownedFocusedElement !== null &&
      (active === ownedFocusedElement || (!ownedFocusedElement.isConnected && active === document.body))
    if (currentFocusTokenRef.current === token) {
      currentSubIssueIdRef.current = operation.previousCurrentIssueId
      currentFocusTokenRef.current = null
      setCurrentSubIssueId(operation.previousCurrentIssueId)
    }
    if (focusRequestRef.current?.token === token) {
      focusRequestRef.current = null
      setFocusRequest(null)
    }

    const fallback = operation.expectedFocus
    if (shouldRestoreFocus && fallback instanceof HTMLElement) {
      requestAnimationFrame(() => {
        if (fallback.isConnected && (document.activeElement === active || document.activeElement === document.body)) {
          fallback.focus()
        }
      })
    }
  }, [])
  const setCurrentSubIssue = useCallback((issueId: string, element?: Element | null) => {
    const request = focusRequestRef.current
    const operation = request?.issueId === issueId ? focusOperationsRef.current.get(request.token) : undefined
    if (operation) {
      operation.focusedElement = element ?? null
      currentFocusTokenRef.current = operation.token
    } else {
      currentFocusTokenRef.current = null
    }
    currentSubIssueIdRef.current = issueId
    setCurrentSubIssueId(issueId)
  }, [])
  useEffect(() => {
    if (!focusRequest) return
    const timeout = window.setTimeout(() => completeFocusRequest(focusRequest.token, false), 2000)
    return () => window.clearTimeout(timeout)
  }, [completeFocusRequest, focusRequest])

  const stateValue = useMemo(
    () => ({
      createDialogOpen,
      activeIssueId,
      currentSubIssueId,
      focusRequest,
      closeCreateDialog,
      openCreateDialog,
      focusSubIssue,
      claimFocusRequest,
      completeFocusRequest,
      remapSubIssueFocus,
      rollbackSubIssueFocus,
      setCurrentSubIssue,
    }),
    [
      activeIssueId,
      claimFocusRequest,
      closeCreateDialog,
      completeFocusRequest,
      createDialogOpen,
      focusSubIssue,
      focusRequest,
      currentSubIssueId,
      openCreateDialog,
      remapSubIssueFocus,
      rollbackSubIssueFocus,
      setCurrentSubIssue,
    ],
  )

  return <SubIssueStateContext value={stateValue}>{children}</SubIssueStateContext>
}

export const useSubIssueState = (): SubIssueStateContext => {
  const context = use(SubIssueStateContext)
  if (!context) {
    throw new Error('useSubIssueState must be used within a SubIssueStateContext')
  }

  return context
}

/** For nested list items that also render outside the issue viewer. */
export const useOptionalSubIssueState = (): SubIssueStateContext | undefined => use(SubIssueStateContext)
